// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { addWatchListPosition, removeWatchListPosition } from './tag-manager-thunk';
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { initialState } from './tag-manager-state';
import {
  tagManagerEntityAdapter,
  tickerTagEntityAdapter,
  watchListEntityAdapter,
} from './tag-manager-selectors';
import { type TickerTagFeature } from '@shared/data-access/interfaces/api/engine/citec';
import { type Tag } from '@shared/data-access/interfaces/redux/tag-manager-state';
import { type WatchListPosition } from '@shared/data-access/interfaces/redux/my-research-state';
import { watchlistApi } from '@shared/data-access/api/engine/watchlist-api';

export const tagManagerSlice = createSlice({
  initialState: () => initialState,
  name: 'tagManager',
  reducers: {
    setSelectTag: (
      state,
      {
        payload: [feature, selectedTag],
      }: PayloadAction<[feature: TickerTagFeature, tag: string]>,
    ) => {
      tagManagerEntityAdapter.updateOne(state.tags, {
        id: feature,
        changes: { selectedTag },
      });
    },
    setTag: (
      state,
      {
        payload: [feature, tag],
      }: PayloadAction<[feature: TickerTagFeature, tag: Tag]>,
    ) => {
      tagManagerEntityAdapter.updateOne(state.tags, {
        id: feature,
        changes: {
          tags: tickerTagEntityAdapter.setOne(
            state.tags.entities[feature]!.tags,
            tag,
          ),
        },
      });
    },
    setTags: (
      state,
      {
        payload: [feature, tags],
      }: PayloadAction<[feature: TickerTagFeature, tags: Tag[]]>,
    ) => {
      tagManagerEntityAdapter.updateOne(state.tags, {
        id: feature,
        changes: {
          tags: tickerTagEntityAdapter.setAll(
            state.tags.entities[feature]!.tags,
            tags,
          ),
        },
      });
    },
    setTagTickers: (
      state,
      {
        payload: [feature, name, tickers],
      }: PayloadAction<
        [feature: TickerTagFeature, name: string, tickers: string[]]
      >,
    ) => {
      tagManagerEntityAdapter.updateOne(state.tags, {
        id: feature,
        changes: {
          tags: tickerTagEntityAdapter.updateOne(
            state.tags.entities[feature]!.tags,
            {
              id: name,
              changes: { tickers },
            },
          ),
        },
      });
    },
    setWatchlistPosition: (
      state,
      { payload }: PayloadAction<WatchListPosition>,
    ) => {
      watchListEntityAdapter.setOne(state.watchlist, payload);
    },
    setWatchlistPositions: (
      state,
      { payload }: PayloadAction<WatchListPosition[]>,
    ) => {
      watchListEntityAdapter.setAll(state.watchlist, payload);
    },
    removeWatchlistPosition: (state, { payload }: PayloadAction<string>) => {
      watchListEntityAdapter.removeOne(state.watchlist, payload);
    },
    resetWatchlist: state => {
      watchListEntityAdapter.removeAll(state.watchlist);
    },
    resetTags: state => {
      tagManagerEntityAdapter.removeAll(state.tags);
    },
    resetTagManager: () => initialState,
  },
  extraReducers: builder => {
    /* istanbul ignore next */
    builder
      .addCase(addWatchListPosition.fulfilled, (state, { payload }) => {
        watchListEntityAdapter.setOne(state.watchlist, payload);
      })
      .addCase(removeWatchListPosition.fulfilled, (state, { payload }) => {
        watchListEntityAdapter.removeOne(state.watchlist, payload);
      })
      .addMatcher(
        watchlistApi.endpoints.getWatchlist.matchFulfilled,
        (state, { payload }) => {
          watchListEntityAdapter.setAll(state.watchlist, payload);
        },
      );
  },
});
